import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import moment from 'moment';
import { toast } from 'react-toastify';

import {
  Anchor,
  Badge,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Table,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import type { RootState } from '../../../app/store';
import styles from './CommonStyling.module.css';

const bulkUpdateUrl =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/v1/studio/thumbnails/bulk-update'
    : 'https://studio-app-api.penpencil.co/v1/studio/thumbnails/bulk-update';

// Same cap as the backend (BULK_THUMBNAIL_MAX_PER_RUN default)
const MAX_SELECTION = 300;

type BulkRow = {
  videoFileId: string;
  notesFileId: string;
  youtubeId: string;
  center: string;
  room?: string;
  startTime: string;
  stopTime?: string;
  videoFileName?: string;
  notesFileName?: string;
  title: string;
};

const formatTime = (time?: string) =>
  time ? moment(time).format('HH:mm:ss') : '--';

const BulkThumbnailUpdate = () => {
  const navigate = useNavigate();
  const { center, from, to } = useSelector((state: RootState) => state.filter);

  const [opened, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [preview, setPreview] = useState<any>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // When user selects a single day (from === to, or to is on the same day at 00:00:00),
  // extend effectiveTo to the end of that day (23:59:59.999) so it covers the entire day.
  const getEffectiveDates = () => {
    let effectiveFrom = from;
    let effectiveTo = to;
    if (from && to) {
      const fromMoment = moment(from);
      const toMoment = moment(to);
      if (
        fromMoment.isSame(toMoment) ||
        (fromMoment.isSame(toMoment, 'day') &&
          toMoment.hours() === 0 &&
          toMoment.minutes() === 0)
      ) {
        effectiveTo = toMoment.endOf('day').toISOString();
      }
    }
    return { effectiveFrom, effectiveTo };
  };

  // Modal blocks the filters, so confirm sends the same range as the preview
  const callBulkUpdate = async (
    dryRun: boolean,
    items?: { videoFileId: string; notesFileId: string }[]
  ) => {
    const { effectiveFrom, effectiveTo } = getEffectiveDates();
    const response = await fetch(bulkUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('id_token') as string,
      },
      body: JSON.stringify({
        from: effectiveFrom,
        to: effectiveTo,
        center: center[0],
        dryRun,
        items,
      }),
    });

    return { ok: response.ok, json: await response.json() };
  };

  const handleError = () => {
    toast.error('Something went wrong');
    localStorage.clear();
    navigate('/login');
  };

  const handlePreviewClick = async () => {
    if (!from || !to) {
      toast.error('Select a date range first');
      return;
    }

    const { effectiveFrom, effectiveTo } = getEffectiveDates();
    if (moment(effectiveTo).isBefore(moment(effectiveFrom))) {
      toast.error('"from" date must be earlier than "to" date');
      return;
    }

    const diffHours = moment(effectiveTo).diff(moment(effectiveFrom), 'hours', true);
    if (diffHours > 24) {
      toast.error('Date range cannot exceed 1 day');
      return;
    }

    if (center.length > 1) {
      toast.error('Select only one center');
      return;
    }

    try {
      setIsLoading(true);
      const { ok, json } = await callBulkUpdate(true);
      setIsLoading(false);

      if (ok) {
        setPreview(json.data);
        setSelected(new Set());
        open();
      } else {
        toast.error(json.message ?? 'Something went wrong', {
          autoClose: 5000,
        });
      }
    } catch (error) {
      handleError();
    }
  };

  const rows: BulkRow[] = preview?.sample ?? [];
  const selectableCount = Math.min(rows.length, MAX_SELECTION);
  const allSelected = selectableCount > 0 && selected.size === selectableCount;

  const toggleSelectAll = () => {
    setSelected(
      allSelected
        ? new Set()
        : new Set(rows.slice(0, MAX_SELECTION).map((row) => row.videoFileId))
    );
  };

  const toggleRow = (videoFileId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(videoFileId)) next.delete(videoFileId);
      else if (next.size < MAX_SELECTION) next.add(videoFileId);
      return next;
    });
  };

  const handleConfirmClick = async () => {
    const items = rows
      .filter((row) => selected.has(row.videoFileId))
      .map(({ videoFileId, notesFileId }) => ({ videoFileId, notesFileId }));
    if (items.length === 0) return;

    try {
      setIsLoading(true);
      const { ok, json } = await callBulkUpdate(false, items);
      setIsLoading(false);
      close();

      if (ok) {
        const queuedCount = Number(json.data?.queued) || 0;
        const notEligibleCount = Number(json.data?.notEligible) || 0;
        const failed = items.length - notEligibleCount - queuedCount;
        let message = `${queuedCount} queued`;
        if (failed > 0) message += `, ${failed} failed`;
        if (notEligibleCount > 0)
          message += `, ${notEligibleCount} no longer eligible`;

        toast.success(`${message}. Check back in 15 minutes`, {
          autoClose: 5000,
        });
      } else {
        toast.error(json.message ?? 'Something went wrong', {
          autoClose: 5000,
        });
      }
    } catch (error) {
      handleError();
    }
  };

  const skipReasons = Object.entries(preview?.skipReasons ?? {})
    .filter(([, count]) => Number(count) > 0)
    .map(([reason, count]) => `${reason.replace(/_/g, ' ')}: ${count}`)
    .join(', ');

  return (
    <>
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      <Modal
        opened={opened}
        onClose={close}
        title={
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
            Bulk Thumbnail Update
          </div>
        }
        size='80%'
        centered
        scrollAreaComponent={ScrollArea.Autosize}
      >
        {preview && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
          >
            <Group spacing='xs'>
              <Text weight={600}>
                {moment(preview.from).format('DD MMM YYYY, h:mm a')} to{' '}
                {moment(preview.to).format('DD MMM YYYY, h:mm a')}
              </Text>
              <Badge variant='outline'>{preview.center ?? 'All centers'}</Badge>
            </Group>
            <Group spacing='xs'>
              <Badge variant='light' color='gray'>
                Matched {preview.matchedVideos}
              </Badge>
              <Badge variant='light' color='green'>
                Eligible {preview.eligible}
              </Badge>
              <Badge variant='light' color='orange'>
                Skipped {preview.skipped}
              </Badge>
              <Badge variant='light' color='blue'>
                Selected {selected.size}
              </Badge>
            </Group>
            {skipReasons && (
              <Text size='xs' color='dimmed'>
                Skipped: {skipReasons}
              </Text>
            )}
            <Text size='xs' color='dimmed'>
              Only the date range and center are used. Room, File Type, Batch
              and YouTube ID filters are ignored.
            </Text>
            {rows.length > 0 && (
              <>
                <Text weight={600} size='sm'>
                  Eligible lectures ({rows.length})
                  {rows.length > MAX_SELECTION &&
                    ` - max ${MAX_SELECTION} can be selected at once`}
                </Text>
                <div style={{ maxHeight: 420, overflow: 'auto' }}>
                  <Table
                    striped
                    highlightOnHover
                    withBorder
                    fontSize='xs'
                    verticalSpacing={4}
                  >
                    <thead
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: '#f1efff',
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th>
                          <Checkbox
                            size='xs'
                            aria-label='Select all'
                            checked={allSelected}
                            indeterminate={selected.size > 0 && !allSelected}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th>Center - Room</th>
                        <th>Video File</th>
                        <th>Notes File</th>
                        <th>New Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const isChecked = selected.has(row.videoFileId);
                        return (
                          <tr key={row.videoFileId}>
                            <td>
                              <Checkbox
                                size='xs'
                                aria-label='Select lecture'
                                checked={isChecked}
                                disabled={
                                  !isChecked && selected.size >= MAX_SELECTION
                                }
                                onChange={() => toggleRow(row.videoFileId)}
                              />
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <Text weight={700} size='xs'>
                                {row.center} - {row.room ?? '--'}
                              </Text>
                              <Text size='xs' color='dimmed'>
                                Time: {formatTime(row.startTime)} -{' '}
                                {formatTime(row.stopTime)}
                              </Text>
                            </td>
                            <td>
                              <div>{row.videoFileName ?? '--'}</div>
                              <Anchor
                                href={`https://www.youtube.com/watch?v=${row.youtubeId}`}
                                target='_blank'
                                rel='noreferrer'
                                size='xs'
                              >
                                Open YouTube ↗
                              </Anchor>
                            </td>
                            <td>{row.notesFileName ?? '--'}</td>
                            <td>{row.title}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
            {rows.length === 0 ? (
              <div>Nothing to queue</div>
            ) : (
              <button
                className={styles['common-styling']}
                onClick={handleConfirmClick}
                disabled={isLoading || selected.size === 0}
              >
                {selected.size > 0
                  ? `Queue ${selected.size} lectures`
                  : 'Nothing selected'}
              </button>
            )}
          </div>
        )}
      </Modal>
      <button onClick={handlePreviewClick} className={styles['csv']}>
        Bulk Thumbnail Update
      </button>
    </>
  );
};

export default BulkThumbnailUpdate;
